<template>
  <div class="typing-game" :class="{ 'game-over': gameOver }">
    <!-- 游戏区域 -->
    <div class="game-container" v-if="gameStarted">
      <div class="game-header">
        <div class="score-board">
          <div class="score">得分: {{ score }}</div>
          <div class="high-score">最高分: {{ highScore }}</div>
        </div>
        <div class="stats">
          <div class="lives">❤️ 生命值: {{ 10 - missedCount }}</div>
          <div class="accuracy">正确率: {{ accuracy }}%</div>
          <div class="speed">速度: {{ typingSpeed }} 字/分钟</div>
        </div>
      </div>

      <!-- 游戏主界面 -->
      <div class="game-main">
        <!-- 掉落的字母 -->
        <div
          v-for="(letter, index) in fallingLetters"
          :key="index"
          class="falling-letter"
          :class="{ hit: letter.hit }"
          :data-color="letter.colorType"
          :style="{
            left: letter.x + 'px',
            top: letter.y + 'px',
            animationDuration: letter.speed + 's',
          }"
        >
          {{ letter.char }}
        </div>

        <!-- 爆炸效果 -->
        <div
          v-for="(explosion, index) in explosions"
          :key="'exp-' + index"
          class="explosion"
          :style="{
            left: explosion.x + 'px',
            top: explosion.y + 'px',
          }"
        ></div>
      </div>

      <!-- 游戏控制 -->
      <div class="game-controls">
        <div class="control-buttons-container">
          <button @click="pauseGame" class="control-btn">
            {{ isPaused ? "继续" : "暂停" }}
          </button>
          <button @click="exitGame" class="control-btn">
            退出游戏
          </button>
        </div>
      </div>
    </div>

    <!-- 开始界面 -->
    <div class="start-screen" v-if="!gameStarted">
      <div class="start-content">
        <h1>气球之战🎈</h1>
        <p class="game-description">
          按下键盘上对应的字母，击破掉落的气球！每击中一个得分增加，错过10个气球游戏结束。得分达到500分即可完成挑战！
          <br /><br />
          <span class="keys-hint"
            >当前使用的按键:
            <span class="keys-container">
              <span class="key" v-for="key in displayedKeys" :key="key">{{
                key
              }}</span>
              <span class="keys-ellipsis" v-if="hasMoreKeys">...</span>
            </span>
          </span>
        </p>

        <!-- 优化难度选择 UI -->
        <div class="difficulty-selection">
          <h3>选择难度</h3>
          <div class="difficulty-buttons">
            <button
              v-for="(level, index) in difficultyLevels"
              :key="index"
              @click="selectedDifficulty = index"
              class="difficulty-btn"
              :class="{ active: selectedDifficulty === index }"
            >
              {{ level }}
              <div class="difficulty-icon">{{ difficultyIcons[index] }}</div>
            </button>
          </div>
          <div class="difficulty-description">
            {{ difficultyDescriptions[selectedDifficulty] }}
          </div>
        </div>

        <button @click="startGame" class="start-btn">开始游戏</button>
        <button @click="backToMap" class="back-btn">返回</button>
      </div>
    </div>

    <!-- 游戏结束界面 -->
    <div class="game-over-screen" v-if="gameOver">
      <div class="game-over-content">
        <h2>{{ score >= 500 ? "恭喜完成挑战！🎉" : "游戏结束" }}</h2>

        <div class="typing-report">
          <h3>打字报告</h3>

          <div class="report-grid">
            <div class="report-item">
              <div class="report-icon">🎯</div>
              <div class="report-label">正确率</div>
              <div class="report-value">{{ accuracy }}%</div>
            </div>

            <div class="report-item">
              <div class="report-icon">⚡</div>
              <div class="report-label">打字速度</div>
              <div class="report-value">{{ typingSpeed }} 字/分钟</div>
            </div>

            <div class="report-item">
              <div class="report-icon">⏱️</div>
              <div class="report-label">游戏时长</div>
              <div class="report-value">{{ gameDuration }}</div>
            </div>

            <div class="report-item">
              <div class="report-icon">🎮</div>
              <div class="report-label">总按键次数</div>
              <div class="report-value">{{ totalAttempts }}</div>
            </div>

            <div class="report-item">
              <div class="report-icon">✅</div>
              <div class="report-label">正确按键</div>
              <div class="report-value">{{ correctAttempts }}</div>
            </div>

            <div class="report-item">
              <div class="report-icon">❌</div>
              <div class="report-label">错误按键</div>
              <div class="report-value">
                {{ totalAttempts - correctAttempts }}
              </div>
            </div>
          </div>

          <div class="score-summary">
            <div class="final-score">
              最终得分: <span>{{ score }}</span>
            </div>
            <div class="high-score">
              最高得分: <span>{{ highScore }}</span>
            </div>
          </div>
        </div>

        <div class="game-over-buttons">
          <button @click="restartGame" class="restart-btn">再玩一次</button>
          <button @click="backToMap" class="back-btn">返回</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "BalloonGame",
  data() {
    return {
      gameStarted: false,
      gameOver: false,
      isPaused: false,
      score: 0,
      highScore: 0,
      accuracy: 100,
      typingSpeed: 0,
      fallingLetters: [],
      explosions: [],
      possibleLetters: [],
      displayedKeys: [],
      hasMoreKeys: false,
      levelId: "",
      totalAttempts: 0,
      correctAttempts: 0,
      gameInterval: null,
      startTime: null,
      spawnRate: 1200,
      baseSpeed: 50,
      speedMultiplier: 2,
      missedCount: 0,
      maxLetters: 8,
      backgroundMusic: null,
      gameDuration: "00:00",
      pauseStartTime: null,
      totalPausedTime: 0,
      difficultyLevels: ["简单", "中等", "困难"],
      difficultyIcons: ["🌱", "🌿", "🔥"],
      difficultyDescriptions: [
        "适合初学者，气球下落速度较慢，生成间隔较长。",
        "适合有一定打字基础的玩家，气球下落速度适中。",
        "挑战高手，气球下落速度快，生成间隔短，需要快速反应！",
      ],
      selectedDifficulty: 0,
    };
  },
  methods: {
    getRouteParams() {
      const { keys, levelId } = this.$route.query;

      this.levelId = levelId || "intro21";

      if (keys) {
        this.possibleLetters = keys.split("");
      } else {
        this.possibleLetters = ["a", "s", "d", "f", "j", "k", "l", ";"];
      }

      this.calculateDisplayedKeys();
    },

    calculateDisplayedKeys() {
      const maxKeysToShow = 8;

      if (this.possibleLetters.length <= maxKeysToShow) {
        this.displayedKeys = [...this.possibleLetters];
        this.hasMoreKeys = false;
      } else {
        this.displayedKeys = this.possibleLetters.slice(0, maxKeysToShow);
        this.hasMoreKeys = true;
      }
    },

    startGame() {
      this.gameStarted = true;
      this.gameOver = false;
      this.score = 0;
      this.missedCount = 0;
      this.fallingLetters = [];
      this.explosions = [];
      this.totalAttempts = 0;
      this.correctAttempts = 0;
      this.startTime = Date.now();
      this.totalPausedTime = 0;
      this.pauseStartTime = null;
      this.spawnRate = [1200, 800, 600][this.selectedDifficulty];
      this.baseSpeed = [50, 70, 90][this.selectedDifficulty];
      this.speedMultiplier = 2;

      this.playBackgroundMusic();

      this.gameInterval = setInterval(() => {
        if (!this.isPaused) {
          this.spawnLetter();
          this.updateDifficulty();
        }
      }, this.spawnRate);

      window.addEventListener("keydown", this.handleKeyPress);

      this.updateInterval = setInterval(() => {
        if (!this.isPaused) {
          this.updateLetterPositions();
          this.updateExplosions();
          this.updateGameDuration();
        }
      }, 100);
    },

    spawnLetter() {
      if (this.fallingLetters.length >= this.maxLetters) {
        return;
      }

      const activeLetters = this.fallingLetters.filter((letter) => !letter.hit)
        .length;
      if (activeLetters >= this.maxLetters) {
        return;
      }

      // 获取游戏区域宽度
      let gameWidth = window.innerWidth;
      const gameMainElement = document.querySelector(".game-main");
      if (gameMainElement) {
        gameWidth = gameMainElement.offsetWidth;
      }

      // 设置边缘安全距离
      const safeMargin = 100;

      // 计算安全的随机位置
      const minX = safeMargin;
      const maxX = gameWidth - safeMargin;
      const x = minX + Math.random() * (maxX - minX);

      const char = this.possibleLetters[
        Math.floor(Math.random() * this.possibleLetters.length)
      ];
      const spawnTime = Date.now();

      // 随机分配一个固定的颜色类型 (1-5)
      const colorType = Math.floor(Math.random() * 5) + 1;

      this.fallingLetters.push({
        char,
        x,
        y: -30,
        hit: false,
        speed: this.baseSpeed / this.speedMultiplier,
        spawnTime,
        counted: false,
        colorType: colorType, // 添加颜色类型属性
      });
    },

    handleKeyPress(event) {
      if (this.isPaused || this.gameOver) return;

      const key = event.key.toLowerCase();
      this.totalAttempts++;

      const targetLetter = this.fallingLetters.find(
        (letter) => !letter.hit && letter.char === key
      );

      if (targetLetter) {
        targetLetter.hit = true;
        this.correctAttempts++;
        this.score += 10;

        const audio = new Audio(
          "https://wugecdn.steam.fun/typefun/music/peng.mp3"
        );
        audio.play();

        this.createExplosion(targetLetter.x, targetLetter.y);

        // 立即移除字母，不显示hit动画
        const index = this.fallingLetters.findIndex((l) => l === targetLetter);
        if (index > -1) {
          this.fallingLetters.splice(index, 1);
        }
      }

      this.updateStats();
    },

    createExplosion(x, y) {
      this.explosions.push({
        x: x + 25,
        y: y + 30,
        createdAt: Date.now(),
      });
    },

    updateExplosions() {
      // 移除超过500毫秒的爆炸效果
      this.explosions = this.explosions.filter((exp) => {
        return Date.now() - exp.createdAt < 500;
      });
    },

    updateStats() {
      this.accuracy =
        Math.round((this.correctAttempts / this.totalAttempts) * 100) || 0;
      const timeElapsed = (Date.now() - this.startTime) / 1000 / 60;
      this.typingSpeed = Math.round(this.correctAttempts / timeElapsed) || 0;
    },

    updateDifficulty() {
      if (this.score > 0 && this.score % 100 === 0) {
        this.spawnRate = Math.max(800, this.spawnRate - 50);
        this.speedMultiplier += 0.3;

        clearInterval(this.gameInterval);
        this.gameInterval = setInterval(() => {
          if (!this.isPaused) {
            this.spawnLetter();
            this.updateDifficulty();
          }
        }, this.spawnRate);
      }
    },

    checkGameOver() {
      for (let letter of this.fallingLetters) {
        if (letter.y >= window.innerHeight - 100 && !letter.hit) {
          this.endGame();
          break;
        }
      }
    },

    endGame(isSuccess = false) {
      this.gameOver = true;
      clearInterval(this.gameInterval);
      clearInterval(this.updateInterval);
      window.removeEventListener("keydown", this.handleKeyPress);

      // 最后更新一次游戏时长
      this.updateGameDuration();

      const highScoreKey = `highScore_${this.levelId}`;
      if (this.score > this.highScore) {
        this.highScore = this.score;
        localStorage.setItem(highScoreKey, this.highScore);
      }

      // 如果得分达到500分或者得分>=100且正确率>=80%，则标记为完成
      if (isSuccess || (this.score >= 100 && this.accuracy >= 80)) {
        const completedLevels = JSON.parse(
          localStorage.getItem("completedLevels") || "[]"
        );
        if (!completedLevels.includes(this.levelId)) {
          completedLevels.push(this.levelId);
          localStorage.setItem(
            "completedLevels",
            JSON.stringify(completedLevels)
          );
        }
      }

      if (this.backgroundMusic) {
        this.backgroundMusic.pause();
        this.backgroundMusic = null;
      }

      // 如果是成功完成（得分达到500），播放成功音效
      if (isSuccess) {
        const successAudio = new Audio(
          "https://wugecdn.steam.fun/typefun/music/success.mp3"
        );
        successAudio.play();
      }
    },

    restartGame() {
      this.gameOver = false;
      this.startGame();
    },

    pauseGame() {
      this.isPaused = !this.isPaused;

      if (this.isPaused) {
        // 记录暂停开始时间
        this.pauseStartTime = Date.now();
        if (this.backgroundMusic) {
          this.backgroundMusic.pause();
        }
      } else {
        // 计算暂停的总时间
        if (this.pauseStartTime) {
          this.totalPausedTime += Date.now() - this.pauseStartTime;
          this.pauseStartTime = null;
        }
        if (this.backgroundMusic) {
          this.backgroundMusic.play();
        }
      }
    },

    exitGame() {
      clearInterval(this.gameInterval);
      clearInterval(this.updateInterval);
      window.removeEventListener("keydown", this.handleKeyPress);

      if (this.backgroundMusic) {
        this.backgroundMusic.pause();
        this.backgroundMusic = null;
      }

      this.gameStarted = false;
      this.gameOver = false;
    },

    backToMap() {
      this.$router.go(-1);
    },

    updateLetterPositions() {
      const gameHeight = document.querySelector(".game-main").offsetHeight;

      this.fallingLetters = this.fallingLetters.filter((letter) => {
        const elapsedTime = Date.now() - letter.spawnTime;
        const newY = -30 + elapsedTime / (letter.speed * 3);
        return newY < gameHeight + 50 && (!letter.hit || letter.y < gameHeight);
      });

      this.fallingLetters.forEach((letter) => {
        const elapsedTime = Date.now() - letter.spawnTime;
        letter.y = -30 + elapsedTime / (letter.speed * 3);

        if (letter.y >= gameHeight - 50 && !letter.hit && !letter.counted) {
          letter.counted = true;
          this.missedCount++;

          const audio = new Audio(
            "https://wugecdn.steam.fun/typefun/music/fail.mp3"
          );
          audio.play();

          if (this.missedCount >= 10) {
            this.endGame();
          }
        }
      });

      // 检查得分是否达到500分
      if (this.score >= 500) {
        this.endGame(true);
      }
    },

    createStars() {
      const gameContainer = document.querySelector(".typing-game");
      if (!gameContainer) return;

      for (let i = 0; i < 50; i++) {
        const star = document.createElement("div");
        star.classList.add("star");

        // 随机位置
        const x = Math.random() * window.innerWidth;
        const y = Math.random() * window.innerHeight;

        // 随机大小
        const size = Math.random() * 3 + 1;

        // 随机动画延迟
        const delay = Math.random() * 3;

        star.style.left = `${x}px`;
        star.style.top = `${y}px`;
        star.style.width = `${size}px`;
        star.style.height = `${size}px`;
        star.style.animationDelay = `${delay}s`;

        gameContainer.appendChild(star);
      }
    },

    playBackgroundMusic() {
      if (this.backgroundMusic) {
        this.backgroundMusic.pause();
        this.backgroundMusic = null;
      }

      this.backgroundMusic = new Audio(
        "https://wugecdn.steam.fun/typefun/music/balloon.mp3"
      );
      this.backgroundMusic.loop = true;
      this.backgroundMusic.volume = 0.5;
      this.backgroundMusic.play();
    },

    updateGameDuration() {
      // 计算实际游戏时长（减去暂停的时间）
      const currentTime = Date.now();
      const pauseTime = this.pauseStartTime
        ? currentTime - this.pauseStartTime
        : 0;
      const totalGameTime =
        currentTime - this.startTime - this.totalPausedTime - pauseTime;

      this.gameDuration = this.formatDuration(totalGameTime);
    },

    formatDuration(duration) {
      const minutes = Math.floor(duration / 60000);
      const seconds = Math.floor((duration % 60000) / 1000);
      return `${minutes
        .toString()
        .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    },
  },
  mounted() {
    this.getRouteParams();

    const highScoreKey = `highScore_${this.levelId}`;
    const savedHighScore = localStorage.getItem(highScoreKey);
    if (savedHighScore) {
      this.highScore = parseInt(savedHighScore);
    }

    this.createStars();
  },
  beforeDestroy() {
    clearInterval(this.gameInterval);
    clearInterval(this.updateInterval);
    window.removeEventListener("keydown", this.handleKeyPress);

    const stars = document.querySelectorAll(".star");
    stars.forEach((star) => star.remove());

    if (this.backgroundMusic) {
      this.backgroundMusic.pause();
      this.backgroundMusic = null;
    }
  },
  watch: {
    "$route.query": {
      handler() {
        this.getRouteParams();

        if (this.gameStarted && !this.gameOver) {
          this.exitGame();
          this.startGame();
        }

        const highScoreKey = `highScore_${this.levelId}`;
        const savedHighScore = localStorage.getItem(highScoreKey);
        if (savedHighScore) {
          this.highScore = parseInt(savedHighScore);
        } else {
          this.highScore = 0;
        }
      },
      immediate: true,
    },
    possibleLetters: {
      handler() {
        this.calculateDisplayedKeys();
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
.typing-game {
  height: 100%;
  color: white;
  position: relative;
  overflow: hidden;
  font-family: "Comic Sans MS", "Marker Felt", fantasy;
}

.game-container {
  padding: 20px;
  position: relative;
  z-index: 1;
}

.game-header {
  display: flex;
  justify-content: space-between;
  padding: 20px;
  background: #44c1c7;
  border-radius: 15px;
  margin-bottom: 20px;
  box-shadow: 0 0 15px rgba(0, 255, 255, 0.5);
  backdrop-filter: blur(5px);
  border: 2px solid #44c1c7;
}

.score-board,
.stats {
  display: flex;
  gap: 20px;
}

.score,
.high-score {
  font-size: 20px;
  font-weight: bold;
  color: #ffeb3b;
  text-shadow: 0 0 5px #ff9800;
}

.accuracy,
.speed,
.lives {
  font-size: 18px;
  font-weight: bold;
  color: #e0f7fa;
  text-shadow: 0 0 5px #00bcd4;
}

.game-main {
  height: 66vh;
  position: relative;
  border: 2px solid #00bcd4;
  border-radius: 15px;
  overflow: hidden;
  backdrop-filter: blur(2px);
  background: url("https://steamfun-cdn.oss-cn-zhangjiakou.aliyuncs.com/typefun/images/balloon/balloon.png")
    center/cover no-repeat;
}

.falling-letter {
  position: absolute;
  font-size: 24px;
  font-weight: bold;
  color: #fff;
  background: none;
  padding: 30px;
  border-radius: 50%;
  will-change: transform, opacity;
  text-shadow: 0 0 2px #fff;
  display: flex;
  justify-content: center;
  align-items: center;
}

.falling-letter::before {
  content: "";
  position: absolute;
  width: 50px;
  height: 60px;
  border-radius: 50%;
  z-index: -1;
  animation: float 3s infinite ease-in-out;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.falling-letter[data-color="1"]::before {
  background: #ff5252;
}

.falling-letter[data-color="2"]::before {
  background: #ffeb3b;
}

.falling-letter[data-color="3"]::before {
  background: #4caf50;
}

.falling-letter[data-color="4"]::before {
  background: #2196f3;
}

.falling-letter[data-color="5"]::before {
  background: #9c27b0;
}

.falling-letter::after {
  content: "";
  position: absolute;
  bottom: -10px;
  width: 4px;
  height: 15px;
  background: rgba(255, 255, 255, 0.7);
  z-index: -1;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(-5px) rotate(-2deg);
  }
  50% {
    transform: translateY(5px) rotate(2deg);
  }
}

/* 移除hit动画效果 */
.falling-letter.hit {
  display: none;
}

/* 爆炸效果 */
.explosion {
  position: absolute;
  width: 60px;
  height: 60px;
  background: rgba(255, 215, 0, 0.9);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  animation: explode 0.5s ease-out forwards;
  z-index: 50;
}

@keyframes explode {
  0% {
    transform: translate(-50%, -50%) scale(0.3);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) scale(2);
    opacity: 0;
  }
}

.game-controls {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  gap: 20px;
}

.control-buttons-container {
  display: flex;
  justify-content: center;
  gap: 20px;
  width: 100%;
  max-width: 400px; /* 限制按钮容器的最大宽度 */
}

.control-btn {
  padding: 12px 25px;
  font-size: 18px;
  border: none;
  border-radius: 50px;
  background: #41c2cb;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(3, 169, 244, 0.5);
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 1px;
  flex: 1; /* 让按钮平均分配空间 */
  max-width: 180px; /* 限制按钮最大宽度 */
}

.control-btn:hover {
  background: #03a9f4;
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(3, 169, 244, 0.7);
}

.control-btn:active {
  transform: translateY(1px);
}

/* 优化开始界面样式 */
.start-screen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  z-index: 1000;
}

.start-content {
  background: rgba(255, 255, 255, 0.15);
  padding: 30px;
  border-radius: 24px;
  text-align: center;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 10px 40px rgba(64, 158, 255, 0.6),
    0 0 20px rgba(0, 255, 255, 0.4) inset;
  border: 2px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(15px);
  animation: floatIn 0.7s cubic-bezier(0.2, 0.8, 0.2, 1);
  overflow: hidden;
  position: relative;
}

.start-content::before {
  content: "";
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.15) 0%,
    transparent 60%
  );
  opacity: 0.5;
  pointer-events: none;
  animation: shimmer 8s infinite linear;
  z-index: -1;
}

@keyframes shimmer {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes floatIn {
  0% {
    opacity: 0;
    transform: scale(0.9) translateY(20px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.start-content h1 {
  color: #fff;
  margin-bottom: 20px;
  font-size: 36px;
  text-shadow: 0 0 15px rgba(0, 255, 255, 0.8), 0 0 30px rgba(0, 188, 212, 0.6);
  letter-spacing: 1px;
  font-weight: bold;
  position: relative;
  display: inline-block;
}

.start-content h1::after {
  content: "🎈";
  position: absolute;
  top: -10px;
  right: -30px;
  font-size: 28px;
  animation: float 3s infinite ease-in-out;
}

.game-description {
  text-align: left;
  margin: 20px 0;
  background: rgba(255, 255, 255, 0.08);
  padding: 20px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  font-size: 15px;
  line-height: 1.5;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1) inset;
  color: #e0f7fa;
  max-height: 180px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
}

.game-description::-webkit-scrollbar {
  width: 4px;
}

.game-description::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 10px;
}

.game-description::-webkit-scrollbar-track {
  background: transparent;
}

.keys-hint {
  display: block;
  margin-top: 15px;
  font-weight: 500;
  color: #e0f7fa;
  text-shadow: 0 0 5px rgba(0, 255, 255, 0.6);
  font-size: 14px;
}

.keys-container {
  display: inline-flex;
  max-width: 100%;
  overflow: hidden;
  flex-wrap: nowrap;
}

.keys-ellipsis {
  display: inline-block;
  margin-left: 5px;
  color: #ffeb3b;
  font-weight: bold;
}

.key {
  display: inline-block;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  padding: 4px 8px;
  margin: 0 3px;
  font-family: monospace;
  font-size: 16px;
  color: #ffeb3b;
  text-shadow: 0 0 5px rgba(255, 152, 0, 0.8);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
}

.key:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 188, 212, 0.7);
  background: rgba(255, 255, 255, 0.25);
}

.difficulty-selection {
  margin: 20px 0;
  color: #fff;
  background: rgba(255, 255, 255, 0.08);
  padding: 18px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1) inset;
  animation: fadeIn 0.5s ease-out 0.3s both;
}

.difficulty-selection h3 {
  margin-top: 0;
  margin-bottom: 15px;
  font-size: 18px;
  color: #e0f7fa;
  text-shadow: 0 0 5px rgba(0, 255, 255, 0.6);
  text-align: center;
  font-weight: 600;
}

.difficulty-buttons {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 12px;
}

.difficulty-btn {
  padding: 10px 15px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.08);
  color: white;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 75px;
  flex: 1;
  max-width: 100px;
}

.difficulty-btn:hover {
  transform: translateY(-3px);
  background: rgba(255, 255, 255, 0.15);
  box-shadow: 0 5px 15px rgba(0, 188, 212, 0.4);
}

.difficulty-btn.active {
  background: rgba(68, 193, 199, 0.6);
  border-color: rgba(224, 247, 250, 0.7);
  box-shadow: 0 0 15px rgba(0, 255, 255, 0.6);
  transform: translateY(-3px) scale(1.05);
}

.difficulty-icon {
  font-size: 22px;
  margin-top: 6px;
}

.difficulty-description {
  text-align: center;
  font-size: 13px;
  color: #e0f7fa;
  margin-top: 10px;
  min-height: 36px;
  padding: 0 8px;
  line-height: 1.3;
  opacity: 0.9;
}

.start-btn,
.back-btn {
  padding: 12px 28px;
  font-size: 16px;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 12px 8px;
  font-weight: 600;
  letter-spacing: 1px;
  position: relative;
  overflow: hidden;
}

.start-btn {
  background: linear-gradient(135deg, #44c1c7, #409eff);
  color: white;
  box-shadow: 0 5px 15px rgba(0, 188, 212, 0.4);
}

.start-btn::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: 0.5s;
}

.start-btn:hover::before {
  left: 100%;
}

.back-btn {
  background: rgba(255, 255, 255, 0.08);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.start-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0, 188, 212, 0.6);
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-3px);
  box-shadow: 0 5px 15px rgba(255, 255, 255, 0.1);
}

/* 响应式调整 */
@media (max-width: 768px) {
  .start-content {
    padding: 25px;
    width: 85%;
  }

  .start-content h1 {
    font-size: 28px;
  }

  .game-description {
    padding: 15px;
    font-size: 14px;
    max-height: 150px;
  }

  .difficulty-buttons {
    flex-wrap: wrap;
  }

  .difficulty-btn {
    padding: 8px 12px;
    font-size: 14px;
    min-width: 70px;
  }

  .difficulty-icon {
    font-size: 20px;
  }

  .start-btn,
  .back-btn {
    padding: 10px 20px;
    font-size: 15px;
  }
}

@media (max-width: 480px) {
  .start-content {
    padding: 20px;
    width: 90%;
  }

  .start-content h1 {
    font-size: 24px;
  }

  .game-description {
    padding: 12px;
    font-size: 13px;
    max-height: 140px;
  }

  .keys-hint {
    font-size: 13px;
  }

  .key {
    padding: 3px 6px;
    font-size: 14px;
    margin: 0 2px;
  }

  .difficulty-selection {
    padding: 15px;
  }

  .difficulty-buttons {
    gap: 8px;
  }

  .difficulty-btn {
    min-width: 60px;
    padding: 8px 10px;
  }

  .difficulty-description {
    font-size: 12px;
    min-height: 40px;
  }

  .start-btn,
  .back-btn {
    padding: 10px 18px;
    font-size: 14px;
    margin: 10px 5px;
  }
}

/* 添加生命值样式 */
.lives {
  font-size: 18px;
  font-weight: bold;
  color: #ff4444;
  text-shadow: 0 0 5px #f44336;
}

/* 添加星星背景 */
@keyframes twinkle {
  0%,
  100% {
    opacity: 0.3;
  }
  50% {
    opacity: 1;
  }
}

.star {
  position: absolute;
  background-color: white;
  border-radius: 50%;
  animation: twinkle 3s infinite ease-in-out;
  z-index: 1;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .game-header {
    flex-direction: column;
    gap: 10px;
  }

  .score-board,
  .stats {
    justify-content: center;
    flex-wrap: wrap;
  }

  .falling-letter {
    font-size: 22px;
    padding: 8px;
  }

  .start-content h1 {
    font-size: 28px;
  }

  .game-description li {
    font-size: 14px;
  }

  .start-btn,
  .restart-btn,
  .back-btn {
    padding: 12px 24px;
    font-size: 16px;
  }
}

/* 游戏结束界面样式 */
.game-over-screen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  z-index: 1000;
  animation: fadeIn 0.3s ease-out;
}

.game-over-content {
  background: rgba(255, 255, 255, 0.1);
  padding: 30px;
  border-radius: 25px;
  text-align: center;
  max-width: 600px;
  width: 85%;
  box-shadow: 0 0 30px #409eff;
  border: 2px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  animation: scaleIn 0.4s ease-out;
  transform-origin: center;
}

@keyframes scaleIn {
  0% {
    opacity: 0;
    transform: scale(0.9);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

.game-over-content h2 {
  color: #fff;
  margin-bottom: 20px;
  font-size: 32px;
  text-shadow: 0 0 20px rgba(0, 255, 255, 0.8);
  letter-spacing: 2px;
  animation: slideDown 0.5s ease-out;
}

@keyframes slideDown {
  0% {
    opacity: 0;
    transform: translateY(-20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 打字报告样式 */
.typing-report {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 20px;
  margin-bottom: 25px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.typing-report h3 {
  color: #fff;
  font-size: 22px;
  margin-bottom: 15px;
  text-shadow: 0 0 10px rgba(0, 255, 255, 0.6);
  position: relative;
  display: inline-block;
}

.typing-report h3::after {
  content: "";
  position: absolute;
  bottom: -5px;
  left: 50%;
  transform: translateX(-50%);
  width: 50px;
  height: 2px;
  background: linear-gradient(90deg, transparent, #4caf50, transparent);
}

.report-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  margin-bottom: 20px;
}

.report-item {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 12px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
  animation: fadeInUp 0.5s ease-out both;
}

.report-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 188, 212, 0.3);
  background: rgba(255, 255, 255, 0.2);
}

.report-item:nth-child(1) {
  animation-delay: 0.1s;
}
.report-item:nth-child(2) {
  animation-delay: 0.2s;
}
.report-item:nth-child(3) {
  animation-delay: 0.3s;
}
.report-item:nth-child(4) {
  animation-delay: 0.4s;
}
.report-item:nth-child(5) {
  animation-delay: 0.5s;
}
.report-item:nth-child(6) {
  animation-delay: 0.6s;
}

@keyframes fadeInUp {
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.report-icon {
  font-size: 24px;
  margin-bottom: 8px;
}

.report-label {
  color: #e0f7fa;
  font-size: 13px;
  margin-bottom: 4px;
}

.report-value {
  color: #ffeb3b;
  font-size: 20px;
  font-weight: bold;
  text-shadow: 0 0 5px #ff9800;
}

.score-summary {
  display: flex;
  justify-content: space-around;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  animation: fadeIn 0.5s ease-out 0.6s both;
}

.final-score,
.high-score {
  font-size: 16px;
  color: #fff;
}

.final-score span,
.high-score span {
  font-size: 22px;
  font-weight: bold;
  color: #ffeb3b;
  text-shadow: 0 0 5px #ff9800;
  margin-left: 5px;
}

.game-over-buttons {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 15px;
  animation: fadeIn 0.5s ease-out 0.7s both;
}

.restart-btn,
.back-btn {
  padding: 12px 30px;
  font-size: 16px;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: bold;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.restart-btn {
  background: #409eff;
  color: white;
  box-shadow: 0 5px 15px rgba(0, 188, 212, 0.5);
}

.restart-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0, 188, 212, 0.7);
  background: #2196f3;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-3px);
}

/* 响应式设计 - 打字报告 */
@media (max-width: 768px) {
  .game-over-content {
    padding: 20px;
    width: 90%;
  }

  .game-over-content h2 {
    font-size: 26px;
    margin-bottom: 15px;
  }

  .typing-report {
    padding: 15px;
  }

  .report-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .report-icon {
    font-size: 22px;
  }

  .report-value {
    font-size: 18px;
  }

  .score-summary {
    flex-direction: column;
    gap: 10px;
  }

  .restart-btn,
  .back-btn {
    padding: 10px 20px;
    font-size: 15px;
  }
}

@media (max-width: 480px) {
  .game-over-content {
    padding: 15px;
    width: 95%;
  }

  .game-over-content h2 {
    font-size: 22px;
  }

  .typing-report h3 {
    font-size: 18px;
  }

  .report-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .report-item {
    padding: 10px;
  }

  .report-icon {
    font-size: 20px;
  }

  .report-label {
    font-size: 12px;
  }

  .report-value {
    font-size: 16px;
  }

  .game-over-buttons {
    flex-direction: column;
    gap: 10px;
  }

  .restart-btn,
  .back-btn {
    width: 100%;
    padding: 10px;
  }
}
</style>
